import { useCallback, useEffect, useRef, useState } from "react";
import { fetchStockBarchartHoldingsWatchlists } from "../api/stockBarchartHoldingsWatchlists";
import { getMessageForFetchError } from "../utils/apiError";

function watchlistsEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((item, i) => {
    const other = b[i];
    return (
      item.id === other.id &&
      item.name === other.name &&
      item.barchartUrl === other.barchartUrl
    );
  });
}

/**
 * Load saved Barchart Holdings watchlists once per account; explicit reload via returned function.
 */
export function useStockBarchartHoldingsWatchlists({
  accountId,
  serverUrl,
  stockSession,
  enabled = true,
  onError,
}) {
  const [savedWatchlists, setSavedWatchlists] = useState([]);
  const [lastRefreshAt, setLastRefreshAt] = useState("");
  const inflightRef = useRef(null);
  const loadedAccountRef = useRef("");
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const reloadWatchlists = useCallback(async () => {
    if (!enabled || !accountId) return;
    if (inflightRef.current) return inflightRef.current;

    inflightRef.current = (async () => {
      try {
        const { savedWatchlists: next, lastRefreshAt: nextRefresh } =
          await fetchStockBarchartHoldingsWatchlists(serverUrl, stockSession);
        setSavedWatchlists((prev) => (watchlistsEqual(prev, next) ? prev : next));
        if (nextRefresh) setLastRefreshAt(nextRefresh);
        if (accountId) loadedAccountRef.current = accountId;
      } catch (err) {
        onErrorRef.current?.(
          getMessageForFetchError(err, "Failed to load Barchart watchlists")
        );
      } finally {
        inflightRef.current = null;
      }
    })();

    return inflightRef.current;
  }, [enabled, accountId, serverUrl, stockSession]);

  useEffect(() => {
    if (!enabled || !accountId) return;
    if (loadedAccountRef.current === accountId) return;
    reloadWatchlists();
  }, [enabled, accountId, reloadWatchlists]);

  return {
    savedWatchlists,
    lastRefreshAt,
    reloadWatchlists,
  };
}
